import type { FC } from 'react';

/**
 * The owner's Wayfarer lockup: the green W-with-a-pennant mark plus the
 * wordmark, exactly as supplied (viewBox 282.77 × 44.97, so ~6.3:1).
 *
 * Inlined as vector rather than loaded as a file for two reasons: it stays
 * razor-sharp at every size and on every display, and its colours become the
 * site's own. The wordmark draws in `currentColor` instead of the original
 * hard-coded white, so it reads correctly on a light ground too; the mark draws
 * in the brand accent instead of its original `#3bc92f`, so the logo and the
 * interface can never drift into two different greens. On the site's dark look
 * that resolves to exactly the owner's `#00cd56`.
 *
 * Decorative by itself — the header labels it with the brand name.
 */

const MARK_COLOR = 'hsl(var(--primary))';

export const WayfarerLogo: FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 282.77 44.97"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill={MARK_COLOR}
      d="M10.85,1.19l9.01,26.08L29.15,1.08h7.87l9.42,26.31L55.4,1.2h9.61l-14.37,42.44c-.69.02-1.53.03-2.22.03-2.41,0-4.82.17-7.19-.4-8.32-2-13.07-11.15-9.89-19.14.27-.68.67-1.29.96-1.92.02-.05.08-.15,0-.18-1.73.98-3.39,2.13-4.91,3.41-5.74,4.86-10.49,13.02-11.92,17.84-.02.07-.06.24-.15.24L1,1.19h9.85ZM33,23.41l1.61-1.44,3.33,1.47,3.23-10.63-11.74,6.72,2.43,1.09,3.56-2.49-2.99,2.74.56,2.53h0Z"
    />
    <path
      fill={MARK_COLOR}
      d="M32.51,22.73s-4.93,3.2-7.51,12.26c-.58,2.04-1.12,4.39-1.51,7.09-.2,1.4-.3,2.31-.3,2.31h-4.28s.32-1.39.69-2.54c.95-2.95,2.4-6.41,4.01-9.24,3.47-6.12,8.91-9.89,8.91-9.89h-.01Z"
    />
    <g fill="currentColor">
      <path d="M95.98,33.97h5.82c5.38,0,6.62-.88,8.89-6.33l7.17-17.23h-5.41l-5.74,14.16c-1.21,2.96-2.41,4.87-4.72,5.12l-1.13.15V10.45h-4.32l-5.78,14.16c-1.28,3.18-2.3,4.87-4.46,5.12l-1.13.15V10.45h-4.83v23.52h5.85c4.68,0,6.44-1.94,7.9-5.3l2.16-5.67-.18,10.97h-.07Z" />
      <path d="M132.49,33.97h4.83V10.45h-5.85c-5.34,0-6.66.95-8.89,6.33l-7.13,17.23h5.19l1.72-4.24h10.13v4.24-.04ZM132.49,25.96h-8.6l2.49-6.15c1.1-2.67,1.76-5.16,4.83-5.41l1.24-.07v11.63h.04Z" />
      <path d="M150.19,33.97h4.87l.11-8.89,7.68-14.67h-5.12l-5.27,10.35-5.45-10.35h-4.87l8.01,15.58v7.97h.04Z" />
      <path d="M167.24,33.97h4.9v-8.96h10.39v-3.77h-10.39v-4.94c0-.51,0-2.12,2.09-2.12h9.88v-3.77h-13.68c-2.41,0-3.18.84-3.18,3.48v20.08Z" />
      <path d="M202.03,33.97h4.83V10.45h-5.85c-5.34,0-6.66.95-8.89,6.33l-7.13,17.23h5.19l1.72-4.24h10.13v4.24-.04ZM202.03,25.96h-8.6l2.49-6.15c1.39-3.69,1.65-5.05,4.79-5.34l1.32-.07v11.6-.04Z" />
      <path d="M218.16,27.05h4.68l1.46,2.38c2.16,3.48,3.58,4.94,7.06,4.94.55,0,1.17-.04,1.9-.15l-5.27-7.24,1.1-.51c2.78-1.32,3.77-4.83,3.77-7.65,0-1.39-.29-8.34-6.44-8.38h-13.17v23.52h4.9s0-6.91,0-6.91ZM223.9,23.25h-5.74v-9.04h5.74c1.43,0,3.8.59,3.8,4.61s-2.38,4.43-3.8,4.43Z" />
      <path d="M242.12,10.45c-2.41,0-3.18.84-3.18,3.48v16.61c0,2.6.77,3.44,3.18,3.44h14.12v-3.77h-11.41c-1.13,0-.95-1.21-.95-2.12v-3.99h10.57v-3.8h-10.57v-3.99c0-.51-.22-2.12,1.1-2.12h11.27v-3.77h-14.12v.04Z" />
      <path d="M266.89,27.05h4.68l1.46,2.38c2.16,3.48,3.58,4.94,7.06,4.94.55,0,1.17-.04,1.9-.15l-5.27-7.24,1.1-.51c2.78-1.32,3.77-4.83,3.77-7.65,0-1.39-.29-8.34-6.44-8.38h-13.17v23.52h4.9s0-6.91,0-6.91ZM272.59,23.25h-5.74v-9.04h5.74c1.43,0,3.8.59,3.8,4.61s-2.38,4.43-3.8,4.43Z" />
    </g>
  </svg>
);
